import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import type { GroupAccess } from '../../../api/group/groupAdminApiSlice';
import { useGetOrganizationRoomAccessQuery } from '../../../api/organization/organizationApiSlice';
import { tenantSelector } from '../../../selector/authenticationSelector';
import GrantWindowsEditor from './GrantWindowsEditor';
import WindowPauseSection from './WindowPauseSection';
import useSeriesSeason from './useSeriesSeason';
import { windowEditorOffered } from './windowEditorOffered';

/**
 * The same window editor, reached from the room (M4 spec §6): one unit per
 * organisation whose access to this room carries windows (`windowEditorOffered`). The grant is looked up on the
 * organisation's own room list, so this side reads exactly what the
 * organisation's page edits.
 */
const OrganizationWindowsOnRoom: React.FC<{
    organizationId: string;
    organizationName: string;
    groupId: string;
    useCases: readonly string[];
    season: { from: string, to: string } | null;
}> = ({ organizationId, organizationName, groupId, useCases, season }) => {
    const { t } = useTranslation();
    const tenant = useSelector(tenantSelector);
    const { data } = useGetOrganizationRoomAccessQuery({ organizationId }, { skip: !tenant });
    const grant = data?.items.find((candidate) => candidate.groupId === groupId
        && windowEditorOffered(candidate.accessMode, useCases));
    if (!grant) return null;
    return (
        <GrantWindowsEditor
            organizationId={organizationId}
            grantId={grant.id}
            title={t('allocation_windows.editor.title_organization', { name: organizationName })}
            season={season}
        />
    );
};

interface RoomAllocationWindowsProps {
    groupId: string;
    /** The room's use cases (`Group.compartmentTypes`): open access gets windows only on a hall. */
    useCases: readonly string[];
    access?: GroupAccess;
}

const RoomAllocationWindows: React.FC<RoomAllocationWindowsProps> = ({ groupId, useCases, access }) => {
    const { t } = useTranslation();
    const season = useSeriesSeason();
    // A dedicated room is one organisation's all the time: no windows.
    if (!access || access.accessMode === 'dedicated') return null;
    return (
        <>
            {access.organizationIds.map((organizationId) => (
                <OrganizationWindowsOnRoom
                    key={organizationId}
                    organizationId={organizationId}
                    organizationName={access.organizations?.find((o) => o.id === organizationId)?.name
                        || t('common.not_applicable')}
                    groupId={groupId}
                    useCases={useCases}
                    season={season}
                />
            ))}
            {/* D7: whether this room's windows pause on holidays. */}
            {access.organizationIds.length > 0 && <WindowPauseSection groupId={groupId} />}
        </>
    );
};

export default RoomAllocationWindows;
